// The device library's files: a copy of each opened song in the Origin Private File System, named by the SHA-256 of its bytes.
// OPFS can be missing (older browsers, some private windows) or full; every function says so with a result, never throws at the
// caller, and the app carries on without the copy. The names and states of kept songs live in `meta` (ui/library.ts).

export type KeepResult =
  | { readonly kind: 'kept'; readonly bytes: number }
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'full' };

async function root(): Promise<FileSystemDirectoryHandle | null> {
  try {
    return (await navigator.storage?.getDirectory?.()) ?? null;
  } catch {
    return null;
  }
}

async function songsDir(create: boolean): Promise<FileSystemDirectoryHandle | null> {
  const r = await root();
  if (!r) return null;
  try {
    return await r.getDirectoryHandle('songs', { create });
  } catch {
    return null;
  }
}

type WritableHandle = FileSystemFileHandle & { createWritable?: () => Promise<FileSystemWritableFileStream> };

const isFull = (e: unknown): boolean => e instanceof DOMException && (e.name === 'QuotaExceededError' || e.name === 'NotAllowedError' && /quota|space/i.test(e.message));

/** Write a copy of `file` under `id`. A partial file is removed on failure. */
export async function keepCopy(id: string, file: Blob): Promise<KeepResult> {
  const dir = await songsDir(true);
  if (!dir) return { kind: 'unavailable' };
  let handle: WritableHandle;
  try {
    handle = (await dir.getFileHandle(id, { create: true })) as WritableHandle;
    if (typeof handle.createWritable !== 'function') {
      await dir.removeEntry(id).catch(() => {});
      return { kind: 'unavailable' };
    }
    const w = await handle.createWritable();
    try {
      await w.write(file);
      await w.close();
    } catch (e) {
      await w.abort().catch(() => {});
      throw e;
    }
    return { kind: 'kept', bytes: (await handle.getFile()).size };
  } catch (e) {
    await dir.removeEntry(id).catch(() => {});
    return isFull(e) ? { kind: 'full' } : { kind: 'unavailable' };
  }
}

/** The kept copy as a File called `name`, or null when there is none (or it cannot be read). */
export async function readCopy(id: string, name: string, type: string): Promise<File | null> {
  const dir = await songsDir(false);
  if (!dir) return null;
  try {
    const f = await (await dir.getFileHandle(id)).getFile();
    return new File([f], name, { type: type || f.type });
  } catch {
    return null;
  }
}

/** Delete the kept copy. Resolves false only when the copy could not be removed. */
export async function removeCopy(id: string): Promise<boolean> {
  const dir = await songsDir(false);
  if (!dir) return true;
  try {
    await dir.removeEntry(id);
    return true;
  } catch (e) {
    return e instanceof DOMException && e.name === 'NotFoundError';
  }
}

/** Ask the browser to keep this site's storage through low-disk clean-ups. Once per device, after the first song is kept. */
export async function askPersistentOnce(): Promise<boolean | null> {
  const KEY = 'salsacount.persist-asked';
  try {
    if (localStorage.getItem(KEY)) return null;
    localStorage.setItem(KEY, '1');
  } catch {
    return null;
  }
  try {
    return (await navigator.storage?.persist?.()) ?? null;
  } catch {
    return null;
  }
}
